import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Country, CountryPartyType } from "../../../../api/Api";
import dayjs from "dayjs";
import {
  COUNTRY_PARTY_LABELS,
  getSelectedParties
} from "../mftTasks/parties/utils";

type Row = [string, string];

const fmt = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const section = (
  doc: jsPDF,
  title: string,
  rows: Row[],
  startY: number
): number => {
  autoTable(doc, {
    head: [[title, ""]],
    body: rows.filter((row) => row[0] !== "" && row[1] !== ""),
    startY,
    theme: "grid",
    headStyles: { fillColor: [18, 46, 56], textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: "bold" },
      1: { cellWidth: 110 }
    }
  });
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;
  return finalY + 6;
};

/**
 * The full field-by-field record, unlike the on-screen panel which shows only status chips and notes
 * (ADHO-443). For a country that means every per-party row is written out: which TSO was screened,
 * which exchange has an agreement, what collateral sits where. That detail is the whole point of the
 * document, so nothing here collapses a party list into a count.
 */
export const buildSignOffPdf = (country: Country): jsPDF => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const margin = 14;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(
    `Country addition: ${country.countryName || ""}`,
    margin,
    margin + 4
  );

  let y = margin + 12;

  const marketAccess = country.marketAccessLifecycle;
  const parties = getSelectedParties(marketAccess);

  /** "TSO (Energinet)", or just "TSO" before Market Access has named it. */
  const label = (partyType?: CountryPartyType): string => {
    if (!partyType) return "Unknown party";
    const base = COUNTRY_PARTY_LABELS[partyType] ?? partyType;
    const named = parties.find((party) => party.type === partyType);
    return named?.name ? `${base} (${named.name})` : base;
  };

  y = section(
    doc,
    "Basic Information",
    [
      ["Country", fmt(country.countryName)],
      ["Trading team", fmt(country.tradingTeam)],
      ["MFT contact person", fmt(country.contactPersonTradingTeam)],
      ["Contracting entity", fmt(country.contractingEntity)],
      ["VP", fmt(country.vp?.displayName)],
      ["Product type", fmt(country.productType)],
      ["Venue", fmt(country.venue)],
      ["Settlement type", fmt(country.tradingSettlementType)],
      ["Transaction type", fmt(country.transactionType)],
      ["Mandate granted", fmt(country.mandate)],
      ["Deal unit", fmt(country.unit)],
      [
        "Expected deal volume/size",
        country.dealVolume
          ? `${country.dealVolume} ${country.unit || ""}`.trim()
          : "N/A"
      ],
      country.businessRational
        ? ["Business rational", fmt(country.businessRational)]
        : ["", ""]
    ],
    y
  );

  y = section(
    doc,
    "Market Access",
    [
      ["Assignee", fmt(marketAccess?.marketAccessAssignee)],
      ["Local branch", fmt(marketAccess?.localBranch)],
      ["Local branch name", fmt(marketAccess?.localBranchName)],
      ["Contracting entity name", fmt(marketAccess?.contractingEntityName)],
      ["Trading license", fmt(marketAccess?.tradingLicense)],
      ["License authority", fmt(marketAccess?.licenseAuthorityName)],
      ["Legal consultancy", fmt(marketAccess?.legalConsultancy)],
      ["Legal consultants", fmt(marketAccess?.legalConsultantsName)],
      [
        "Legal consultants contact",
        marketAccess?.legalConsultantsContactName ||
        marketAccess?.legalConsultantsContactEmail
          ? `${marketAccess?.legalConsultantsContactName || ""} ${
              marketAccess?.legalConsultantsContactEmail || ""
            }`.trim()
          : "N/A"
      ],
      ["TSO", fmt(marketAccess?.tso)],
      ["TSO name", fmt(marketAccess?.tsoName)],
      ["Exchange", fmt(marketAccess?.exchange)],
      ["Exchange name", fmt(marketAccess?.exchangeName)],
      ["Bank", fmt(marketAccess?.bank)],
      ["Bank name", fmt(marketAccess?.bankName)],
      ["Clearing bank", fmt(marketAccess?.clearingBank)],
      ["Clearing bank name", fmt(marketAccess?.clearingBankName)],
      ["Contractual MFT team", fmt(marketAccess?.contractualMftTeam)],
      ["NDA review", fmt(marketAccess?.ndaReview)],
      ["Mandate", fmt(marketAccess?.mandate)],
      ["Collateral", fmt(marketAccess?.collateral)],
      ["Trade capture", fmt(marketAccess?.tradeCapture)],
      ["User setup", fmt(marketAccess?.userSetup)],
      marketAccess?.signOffNote
        ? ["Sign-off note", fmt(marketAccess.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const compliance = country.complianceLifecycle;
  y = section(
    doc,
    "Compliance",
    [
      ["KYC check assignee", fmt(compliance?.kycCheckAssignee)],
      ...(compliance?.kycChecks ?? []).map(
        (check): Row => [
          `KYC: ${label(check.partyType)}`,
          check.catalystId
            ? `${fmt(check.status)}, Catalyst ID ${check.catalystId}`
            : fmt(check.status)
        ]
      ),
      ["KYC on MFT assignee", fmt(compliance?.kycCheckOnMftAssignee)],
      ...(compliance?.kycChecksOnMft ?? []).map(
        (check): Row => [
          `KYC on MFT: ${label(check.partyType)}`,
          check.catalystId
            ? `${fmt(check.status)}, Catalyst ID ${check.catalystId}`
            : fmt(check.status)
        ]
      ),
      ["Reporting", fmt(compliance?.reporting)],
      ["Reporting assignee", fmt(compliance?.reportingAssignee)],
      compliance?.signOffNote
        ? ["Sign-off note", fmt(compliance.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const legal = country.legalLifecycle;
  y = section(
    doc,
    "Legal",
    [
      ...(legal?.agreements ?? []).map(
        (agreement): Row => [
          `Agreement: ${label(agreement.partyType)}`,
          [
            fmt(agreement.reviewStatus),
            agreement.assignee ? `assignee ${agreement.assignee}` : "",
            agreement.clearContractUrl
              ? `case ${agreement.clearContractUrl}`
              : ""
          ]
            .filter(Boolean)
            .join(", ")
        ]
      ),
      legal?.signOffNote ? ["Sign-off note", fmt(legal.signOffNote)] : ["", ""]
    ],
    y
  );

  const risk = country.riskLifecycle;
  y = section(
    doc,
    "Risk",
    [
      ["Mandate approval", fmt(risk?.mandateApproval)],
      ["Mandate approval assignee", fmt(risk?.mandateApprovalAssignee)],
      risk?.signOffNote ? ["Sign-off note", fmt(risk.signOffNote)] : ["", ""]
    ],
    y
  );

  const creditRisk = country.creditRiskLifecycle;
  y = section(
    doc,
    "Credit Risk",
    [
      [`Credit check ${label("tso")}`, fmt(creditRisk?.creditCheckTso)],
      ["Credit check TSO assignee", fmt(creditRisk?.creditCheckTsoAssignee)],
      [
        "Credit amount TSO",
        creditRisk?.creditAmountTso
          ? `${creditRisk.creditAmountTso} ${creditRisk.creditCurrencyTso || ""}`.trim()
          : "N/A"
      ],
      [
        `Credit check ${label("exchange")}`,
        fmt(creditRisk?.creditCheckExchange)
      ],
      [
        "Credit check exchange assignee",
        fmt(creditRisk?.creditCheckExchangeAssignee)
      ],
      [
        "Credit amount exchange",
        creditRisk?.creditAmountExchange
          ? `${creditRisk.creditAmountExchange} ${creditRisk.creditCurrencyExchange || ""}`.trim()
          : "N/A"
      ],
      creditRisk?.signOffNote
        ? ["Sign-off note", fmt(creditRisk.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const treasury = country.treasuryLifecycle;
  y = section(
    doc,
    "Treasury",
    [
      ...(treasury?.parties ?? []).map(
        (party): Row => [
          `Agreement review: ${label(party.partyType)}`,
          [
            fmt(party.agreementReview),
            party.agreementReviewAssignee
              ? `assignee ${party.agreementReviewAssignee}`
              : "",
            party.contactPerson ? `contact ${party.contactPerson}` : "",
            party.accountDetails ? `account ${party.accountDetails}` : "",
            party.currency ? `currency ${party.currency}` : ""
          ]
            .filter(Boolean)
            .join(", ")
        ]
      ),
      ...(treasury?.collaterals ?? []).map(
        (collateral): Row => [
          `Collateral: ${label(collateral.partyType)}`,
          [
            fmt(collateral.form),
            collateral.amount
              ? `${collateral.amount} ${collateral.currency || ""}`.trim()
              : "",
            collateral.assignee ? `assignee ${collateral.assignee}` : ""
          ]
            .filter(Boolean)
            .join(", ")
        ]
      ),
      ["Margin call system", fmt(treasury?.marginCallSystem)],
      ["Margin call status", fmt(treasury?.marginCallStatus)],
      ["Cash flow forecast", fmt(treasury?.cashFlowForecast)],
      treasury?.signOffNote
        ? ["Sign-off note", fmt(treasury.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const finance = country.financeLifecycle;
  y = section(
    doc,
    "Finance",
    [
      ...(finance?.parties ?? []).map(
        (party): Row => [
          `Setup: ${label(party.partyType)}`,
          [
            `agreement ${fmt(party.agreementReview)}`,
            `Business Central ${fmt(party.businessCentralSetup)}`,
            party.iban ? `IBAN ${party.iban}` : "",
            party.assignee ? `assignee ${party.assignee}` : ""
          ]
            .filter(Boolean)
            .join(", ")
        ]
      ),
      ["Test trade for settlement method", fmt(finance?.testTrade)],
      ["Callback", fmt(finance?.callback)],
      finance?.signOffNote
        ? ["Sign-off note", fmt(finance.signOffNote)]
        : ["", ""]
    ],
    y
  );

  const tech = country.techLifecycle;
  y = section(
    doc,
    "Technology",
    [
      [
        "Included in existing trade capture",
        fmt(tech?.includedInExistingTradeCapture)
      ],
      ["New trade capture developed status", fmt(tech?.newTradeCaptureStatus)],
      tech?.signOffNote ? ["Sign-off note", fmt(tech.signOffNote)] : ["", ""]
    ],
    y
  );

  const signOff = country.signOff;
  if (signOff?.summary) {
    y = section(doc, "Summary", [["", signOff.summary]], y);
  }

  if (signOff?.signatories?.length) {
    const statusRows: Row[] = [["Status", fmt(signOff.status)]];
    if (signOff.status === "rejected") {
      statusRows.push(["Rejected by", fmt(signOff.rejectedBy)]);
      if (signOff.rejectedAt) {
        statusRows.push([
          "Rejected at",
          dayjs(signOff.rejectedAt).format("LLL")
        ]);
      }
      statusRows.push(["Rejection reason", fmt(signOff.rejectionReason)]);
    }
    const signatoryRows: Row[] = signOff.signatories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => [
        `${s.name}${s.isCeo ? " (CEO)" : ""}`,
        s.signed
          ? `Signed${s.signedAt ? ` on ${dayjs(s.signedAt).format("LLL")}` : ""}`
          : "Pending"
      ]);
    section(doc, "Sign off", [...statusRows, ...signatoryRows], y);
  }

  return doc;
};
